import React from 'react';

const ArrowRightIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
    </svg>
);

const ClockIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

interface BlogPost {
    id: number;
    title: string;
    excerpt: string;
    readTime: string;
    category: string;
    image: string;
}

const featuredPosts: BlogPost[] = [
    {
        id: 1,
        title: "The Ultimate Guide to Choosing a Laptop in 2025",
        excerpt: "Navigate the complex world of laptop specs with our comprehensive guide.",
        readTime: "8 min read",
        category: "Buying Guide",
        image: "https://images.pexels.com/photos/7974/pexels-photo.jpg?auto=compress&cs=tinysrgb&w=800"
    },
    {
        id: 2,
        title: "Gaming Laptops vs Desktop PCs: The 2025 Showdown",
        excerpt: "Explore the pros and cons of gaming laptops versus desktop setups.",
        readTime: "10 min read",
        category: "Gaming",
        image: "https://images.pexels.com/photos/1229861/pexels-photo-1229861.jpeg?auto=compress&cs=tinysrgb&w=800"
    },
    {
        id: 3,
        title: "AI-Powered Laptop Recommendations: How It Works",
        excerpt: "Behind-the-scenes look at how our AI delivers personalized recommendations.",
        readTime: "6 min read",
        category: "Technology",
        image: "https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=800"
    }
];

interface FeaturedBlogSectionProps {
    onNavigate: (page: 'blog') => void;
}

export const FeaturedBlogSection: React.FC<FeaturedBlogSectionProps> = ({ onNavigate }) => {
    return (
        <div className="mt-20 sm:mt-28 max-w-6xl mx-auto">
            <div className="text-center mb-10 sm:mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--brand)]/10 rounded-full mb-4 floating">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[var(--brand)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    <span className="text-xs font-semibold text-[var(--brand)] uppercase tracking-wider">Latest Insights</span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[var(--text-1)] font-serif mb-4 leading-tight">
                    Expert Tech Insights
                </h2>
                <p className="text-[var(--text-2)] text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
                    Stay ahead with our latest guides, comparisons, and industry analysis
                </p>
            </div>

            {/* Mobile scroll indicator */}
            <div className="md:hidden text-center mb-4">
                <p className="text-xs text-[var(--text-3)] flex items-center justify-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                    Swipe to see more
                </p>
            </div>

            <div className="mb-10">
                {/* Desktop grid (visible on md and up) */}
                <div className="hidden md:grid md:grid-cols-3 md:gap-6 sm:gap-8">
                    {featuredPosts.map((post, index) => (
                        <div
                            key={post.id}
                            className="group bg-[var(--surface-2)] rounded-2xl overflow-hidden border border-[var(--surface-4)] shadow-custom hover:shadow-2xl transition-all duration-500 cursor-pointer slide-up"
                            onClick={() => onNavigate('blog')}
                        >
                            <div className="relative overflow-hidden aspect-video bg-[var(--surface-3)]">
                                <img
                                    src={post.image}
                                    alt={post.title}
                                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <span className="absolute top-4 left-4 text-xs font-semibold bg-[var(--brand)] text-[var(--brand-text)] px-3 py-1.5 rounded-full shadow-lg transform -translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                                    {post.category}
                                </span>
                            </div>
                            <div className="p-6">
                                <h3 className="text-lg font-bold text-[var(--text-1)] mb-3 leading-snug group-hover:text-[var(--brand)] transition-colors duration-300 line-clamp-2">
                                    {post.title}
                                </h3>
                                <p className="text-sm text-[var(--text-2)] leading-relaxed mb-4 line-clamp-2">
                                    {post.excerpt}
                                </p>
                                <div className="flex items-center justify-between pt-4 border-t border-[var(--surface-4)]">
                                    <div className="flex items-center gap-2 text-xs text-[var(--text-3)]">
                                        <ClockIcon />
                                        <span>{post.readTime}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-[var(--brand)] font-semibold text-sm opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                                        Read <ArrowRightIcon />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Mobile horizontal scroller (visible on small screens only) */}
                <div className="md:hidden overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory no-scrollbar">
                    <div className="flex gap-4">
                        {featuredPosts.map((post, index) => (
                            <div
                                key={post.id}
                                className="w-72 flex-shrink-0 snap-start"
                                onClick={() => onNavigate('blog')}
                            >
                                <div
                                    className="group bg-[var(--surface-2)] rounded-2xl overflow-hidden border border-[var(--surface-4)] shadow-custom hover:shadow-2xl transition-all duration-500 cursor-pointer"
                                >
                                    <div className="relative overflow-hidden aspect-video bg-[var(--surface-3)]">
                                        <img
                                            src={post.image}
                                            alt={post.title}
                                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                        <span className="absolute top-4 left-4 text-xs font-semibold bg-[var(--brand)] text-[var(--brand-text)] px-3 py-1.5 rounded-full shadow-lg transform -translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                                            {post.category}
                                        </span>
                                    </div>
                                    <div className="p-6">
                                        <h3 className="text-lg font-bold text-[var(--text-1)] mb-3 leading-snug group-hover:text-[var(--brand)] transition-colors duration-300 line-clamp-2">
                                            {post.title}
                                        </h3>
                                        <p className="text-sm text-[var(--text-2)] leading-relaxed mb-4 line-clamp-2">
                                            {post.excerpt}
                                        </p>
                                        <div className="flex items-center justify-between pt-4 border-t border-[var(--surface-4)]">
                                            <div className="flex items-center gap-2 text-xs text-[var(--text-3)]">
                                                <ClockIcon />
                                                <span>{post.readTime}</span>
                                            </div>
                                            <div className="flex items-center gap-1 text-[var(--brand)] font-semibold text-sm opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                                                Read <ArrowRightIcon />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="text-center">
                <button
                    onClick={() => onNavigate('blog')}
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[var(--brand)] to-[var(--accent)] text-[var(--brand-text)] font-semibold rounded-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 pulse-shadow"
                >
                    View All Articles
                    <ArrowRightIcon />
                </button>
            </div>
        </div>
    );
};
